import React, { useState, useEffect, useRef } from "react";
import "./TaskProgress.css";
import { useAuth } from "../../../firebase/AuthProvider";
import { useNavigate, Link } from "react-router-dom";
// Removed Popover import - using custom divs instead
import { Subscription } from "rxjs";
import { getBarGraphData } from "../../../services/weeklySupervision/weeklySupervisionService";
import { selectedUserForTask$ } from "../../../services/Subjects/userSubject";
import ComingSoon from "../../../assets/images/Wavy_Tech-08_Single-02.jpg";

// Types
interface Assignee {
  id: string;
  firstName: string;
  lastName: string;
}

interface Task {
  id: number;
  friendlyId: string;
  title: string;
  assignees: Assignee[];
}

interface StatusData {
  status: string;
  late: number;
  onTime: number;
  lateTasks: Task[];
  onTimeTasks: Task[];
}

interface ApiTaskData {
  subType: string;
  total: number;
  statuses: StatusData[];
}

interface TaskData {
  subType: string;
  total: number;
  late: number;
  inReview: number;
  issuesFound: number;
  notStarted: number;
  lateTasks: Task[];
  inReviewTasks: Task[];
  issuesFoundTasks: Task[];
  notStartedTasks: Task[];
}

const TaskProgress: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, currentRoles, currentOrganization } = useAuth();

  const [selectedTab, setSelectedTab] = useState<string>("MONTHLY");
  const [barGraphData, setBarGraphData] = useState<TaskData[]>([]);
  const [showComingSoon, setShowComingSoon] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Allow undefined (loading) OR null (aggregate)
  const [effectiveUserId, setEffectiveUserId] = useState<string | null | undefined>(undefined);

  const isSC = currentRoles?.some((role) => role.name.toUpperCase() === "SC");

  // Transform new API structure to old format
  const transformApiData = (apiData: ApiTaskData[]): TaskData[] => {
    return apiData.map((item) => {
      let notStarted = 0;
      let notStartedLate = 0;
      let notStartedTasks: Task[] = [];
      let notStartedLateTasks: Task[] = [];

      let issuesFound = 0;
      let issuesFoundLate = 0;
      let issuesFoundTasks: Task[] = [];
      let issuesFoundLateTasks: Task[] = [];

      let inReview = 0;
      let inReviewLate = 0;
      let inReviewTasks: Task[] = [];
      let inReviewLateTasks: Task[] = [];

      item.statuses.forEach((status) => {
        const statusUpper = status.status.toUpperCase();

        if (statusUpper === "IN PROGRESS") {
          notStarted = status.onTime;
          notStartedLate = status.late;
          notStartedTasks = status.onTimeTasks || [];
          notStartedLateTasks = status.lateTasks || [];
        } else if (statusUpper === "ISSUES FOUND") {
          issuesFound = status.onTime;
          issuesFoundLate = status.late;
          issuesFoundTasks = status.onTimeTasks || [];
          issuesFoundLateTasks = status.lateTasks || [];
        } else if (statusUpper.includes("IN REVIEW")) {
          // Combine IN REVIEW (PM) and IN REVIEW (SUP)
          inReview += status.onTime;
          inReviewLate += status.late;
          inReviewTasks = [...inReviewTasks, ...(status.onTimeTasks || [])];
          inReviewLateTasks = [...inReviewLateTasks, ...(status.lateTasks || [])];
        }
      });

      return {
        subType: item.subType,
        total: item.total,
        late: Math.max(notStartedLate, issuesFoundLate, inReviewLate), // Max late across all categories
        inReview: inReview,
        issuesFound: issuesFound,
        notStarted: notStarted,
        lateTasks: [...notStartedLateTasks, ...issuesFoundLateTasks, ...inReviewLateTasks],
        inReviewTasks: inReviewTasks,
        issuesFoundTasks: issuesFoundTasks,
        notStartedTasks: notStartedTasks,
        // Store individual late counts and tasks for each category
        notStartedLate: notStartedLate,
        issuesFoundLate: issuesFoundLate,
        inReviewLate: inReviewLate,
        notStartedLateTasks: notStartedLateTasks,
        issuesFoundLateTasks: issuesFoundLateTasks,
        inReviewLateTasks: inReviewLateTasks,
      } as TaskData & {
        notStartedLate: number;
        issuesFoundLate: number;
        inReviewLate: number;
        notStartedLateTasks: Task[];
        issuesFoundLateTasks: Task[];
        inReviewLateTasks: Task[];
      };
    });
  };

  // Popover state
  const [popoverState, setPopoverState] = useState<{
    show: boolean;
    content: React.ReactNode;
    position: { x: number; y: number };
  }>({
    show: false,
    content: null,
    position: { x: 0, y: 0 },
  });

  const cardRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const hidePopoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe to selected user
  useEffect(() => {
    let userSubscription: Subscription;
    const tokenPromise = currentUser?.getIdToken();

    if (tokenPromise) {
      tokenPromise.then((token) => {
        userSubscription = selectedUserForTask$.subscribe((users) => {
          if (users.length > 0) {
            setEffectiveUserId(users[0].id);
          } else {
            // Null means "Aggregate / No Specific User Selected"
            setEffectiveUserId(null);
          }
        });
      });
    }

    return () => {
      if (userSubscription) userSubscription.unsubscribe();
    };
  }, [currentUser]);

  // Fetch API data
  useEffect(() => {
    const fetchGraphData = async () => {
      // Only return if undefined (initial loading state). Null is valid.
      if (!currentUser || effectiveUserId === undefined) return;

      try {
        setLoading(true);
        const idToken = await currentUser.getIdToken();
        const apiData = await getBarGraphData(idToken, effectiveUserId);
        const transformedData = transformApiData(apiData);
        setBarGraphData(transformedData);
      } catch (error) {
        console.error("Error fetching bar graph data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGraphData();
  }, [currentUser, effectiveUserId]);

  const selectedData = barGraphData.find(
    (data) => data.subType === selectedTab
  );

  const handleNavigation = () => {
    if (
      currentRoles?.some((role) => role.name.toUpperCase() === "SUPERVISOR")
    ) {
      navigate("/weekly-supervision-supervisor");
    } else {
      navigate("/weekly-supervision");
    }
  };

  const getCategoryParams = (subtype: string) => {
    if (subtype.toUpperCase() === "INITIAL") {
      return `tags=Initial`;
    }
    return `type=${subtype.toLowerCase()}`;
  }

  // --- UPDATED CLICK HANDLERS ---

  // 1. Generic Late Click Handler
  const handleLateClick = (subtype: string, statusString: string) => {
    const categoryParam = getCategoryParams(subtype);

    // Status must be included, otherwise we get ALL late tasks regardless of status
    // Handle comma separated statuses (like In Review)
    const encodedStatus = statusString.split(',').map(s => encodeURIComponent(s.trim())).join(',');

    // Ensure we are filtering by Date=Overdue AND Status=X
    let url = `/tasks?${categoryParam}&date=overdue&status=${encodedStatus}&pageNumber=1&pageSize=10`;

    if (effectiveUserId) {
      url += `&assignee=${effectiveUserId}`;
    }

    navigate(url);
  };

  // 2. In Progress (On Time)
  const handleNotStartedClick = (subtype: string) => {
    // CORRECTED: Added "PENDING EXTENSION"
    const statuses = ["IN PROGRESS", "PENDING EXTENSION"];
    const encodedStatus = statuses.map(s => encodeURIComponent(s)).join(",");

    const categoryParam = getCategoryParams(subtype);

    let url = `/tasks?${categoryParam}&status=${encodedStatus}&excludeDate=overdue&pageNumber=1&pageSize=10`;

    if (effectiveUserId) {
      url += `&assignee=${effectiveUserId}`;
    }

    navigate(url);
  };

  // 3. Issues Found (On Time)
  const handleIssuesFoundClick = (subtype: string) => {
    const status = "ISSUES FOUND";
    const encodedStatus = encodeURIComponent(status);
    const categoryParam = getCategoryParams(subtype);

    let url = `/tasks?${categoryParam}&status=${encodedStatus}&excludeDate=overdue&pageNumber=1&pageSize=10`;

    if (effectiveUserId) {
      url += `&assignee=${effectiveUserId}`;
    }

    navigate(url);
  };

  // 4. In Review (On Time)
  const handleInReviewClick = (subtype: string) => {
    // CORRECTED: Added "PENDING DELETION"
    const statuses = ["IN REVIEW (PM)", "IN REVIEW (SUP)", "PENDING DELETION"];
    const encodedStatuses = statuses
      .map((s) => encodeURIComponent(s))
      .join(",");
    const categoryParam = getCategoryParams(subtype);

    let url = `/tasks?${categoryParam}&status=${encodedStatuses}&excludeDate=overdue&pageNumber=1&pageSize=10`;

    if (effectiveUserId) {
      url += `&assignee=${effectiveUserId}`;
    }

    navigate(url);
  };

  const tabs = [
    { label: "Monthly", value: "MONTHLY" },
    { label: "Quarterly", value: "QUARTERLY" },
    { label: "Annual", value: "ANNUAL" },
    { label: "Interim", value: "INTERIM" },
    { label: "Initial", value: "INITIAL" },
  ];

  // ... (Keep the rest of the file EXACTLY as it was, getTaskCount, chart rendering, popovers, etc.)
  const getTaskCount = (type: string) => {
    const data = barGraphData.find((d) => d.subType === type);
    return data?.total ?? 0;
  };

  const getChartMaxValue = () => {
    if (!selectedData) return 40;
    const data = selectedData as TaskData & { notStartedLate?: number; issuesFoundLate?: number; inReviewLate?: number };
    const notStartedLate = data.notStartedLate ?? 0;
    const issuesFoundLate = data.issuesFoundLate ?? 0;
    const inReviewLate = data.inReviewLate ?? 0;
    const values = [
      selectedData.notStarted + notStartedLate,
      selectedData.issuesFound + issuesFoundLate,
      selectedData.inReview + inReviewLate,
    ];
    const maxTotal = Math.max(...values, 1);
    return Math.ceil(maxTotal / 10) * 10;
  };

  const maxChartValue = getChartMaxValue();

  const getYAxisLabels = () => {
    return [
      maxChartValue,
      Math.round(maxChartValue * 0.75),
      Math.round(maxChartValue * 0.5),
      Math.round(maxChartValue * 0.25),
      0,
    ];
  };

  const calculateBarHeight = (value: number) => {
    if (maxChartValue === 0) return 0;
    return (value / maxChartValue) * 100;
  };

  const handleShowPopover = (
    event: React.MouseEvent<HTMLElement>,
    content: React.ReactNode
  ) => {
    if (hidePopoverTimeout.current) {
      clearTimeout(hidePopoverTimeout.current);
    }
    const x = event.clientX;
    const y = event.clientY;

    setPopoverState({
      show: true,
      content,
      position: { x, y },
    });
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    if (popoverState.show && popoverRef.current) {
      let x = event.clientX;
      let y = event.clientY;
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const popoverWidth = popoverRect.width || 320;
      const popoverHeight = popoverRect.height || 200;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 10;

      if (x + popoverWidth / 2 > viewportWidth - padding) {
        x = viewportWidth - popoverWidth / 2 - padding;
      } else if (x - popoverWidth / 2 < padding) {
        x = popoverWidth / 2 + padding;
      }

      if (y - popoverHeight < padding) {
        y = y + 20;
        if (y + popoverHeight > viewportHeight - padding) {
          y = viewportHeight - popoverHeight - padding;
        }
      }

      setPopoverState(prev => ({
        ...prev,
        position: { x, y },
      }));
    }
  };

  const handleHidePopover = () => {
    if (hidePopoverTimeout.current) {
      clearTimeout(hidePopoverTimeout.current);
    }
    hidePopoverTimeout.current = setTimeout(() => {
      setPopoverState({ show: false, content: null, position: { x: 0, y: 0 } });
    }, 100);
  };

  const handlePopoverMouseEnter = () => {
    if (hidePopoverTimeout.current) {
      clearTimeout(hidePopoverTimeout.current);
    }
  };

  const renderPopoverContent = (
    tasks: Task[],
    category: string,
    count: number
  ) => (
    <>
      <div className="progress-popover-header">
        {`${selectedTab} - ${category} (${count})`}
      </div>
      <div className="progress-bar-popover-body">
        {tasks.length > 0 ? (
          <ul
            className="list-unstyled mb-0 progress-popover-list"
            style={{ paddingLeft: 0, maxHeight: "150px", overflowY: "auto" }}
          >
            {tasks.map((task) => (
              <li key={task.friendlyId} className="progress-popover-item">
                <Link
                  to={`/task/${currentOrganization?.abbreviation}-${task.friendlyId}`}
                  className="pop-up-card progress-popover-link"
                  title={`[${currentOrganization?.abbreviation}-${task.friendlyId}] ${task.title}`}
                >
                  <span className="progress-popover-key">[{currentOrganization?.abbreviation}-{task.friendlyId}]</span>
                  <span className="progress-popover-value">{task.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="progress-popover-empty">No tasks in this category.</div>
        )}
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="task-progress-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div
      className="common_card task-progress-container"
      ref={cardRef}
      style={{ position: "relative" }}
    >
      <div className="task-progress-header top">
        <h2 className="task-progress-title top_bar_tittle">
          Task Progress
        </h2>
        <span
          className="weekly-supervision-link cursor-pointer"
          // onClick={handleNavigation}
          onClick={() => setShowComingSoon(!showComingSoon)}
        >
          Weekly Supervision
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="7"
            height="12"
            viewBox="0 0 7 12"
            fill="none"
          >
            <path
              d="M0.699863 0L6.35986 5.66L0.699863 11.32L-0.000137329 10.61L4.94986 5.66L-0.000137329 0.71L0.699863 0Z"
              fill="#7E57C2"
            />
          </svg>
        </span>
      </div>

      {showComingSoon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-[90%] max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl
                          animate-[scaleIn_0.3s_ease-out]">

            <div className="flex justify-center">
              <img
                src={ComingSoon}
                alt="ComingSoon"
                className="w-80 h-80"
              />
            </div>

            <h2 className="mb-2 text-xl font-semibold text-[#7E57C2]">
              Coming Soon!
            </h2>

            <p className="mb-6 text-sm text-gray-600 leading-relaxed">
              The Weekly Supervision feature is currently under development.
              <br />
              We’re crafting something awesome for you!
            </p>

            <button
              onClick={() => setShowComingSoon(false)}
              className="rounded-lg bg-[#7E57C2] px-5 py-2 text-sm font-medium
                         text-white transition hover:bg-[#6a46b3]"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <div className="tabs-container">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            className={`tab-button ${selectedTab === tab.value ? "active" : ""
              }`}
            onClick={() => setSelectedTab(tab.value)}
          >
            {tab.label}
            <span className="tab-count">{getTaskCount(tab.value)}</span>
          </button>
        ))}
      </div>

      <div className="chart-container ">
        <div className="y-axis">
          {getYAxisLabels().map((label, index) => (
            <div key={index} className="y-axis-item">
              <span className="y-axis-label">{label}</span>
              <span className="y-axis-tick"></span>
            </div>
          ))}
          <div className="y-axis-line"></div>
        </div>

        <div className="bars-container">
          {/* In Progress Bar */}
          <div className="bar-group">
            <div className="bar-wrapper">
              {selectedData && (() => {
                const data = selectedData as TaskData & {
                  notStartedLate?: number;
                  issuesFoundLate?: number;
                  inReviewLate?: number;
                  notStartedLateTasks?: Task[];
                  issuesFoundLateTasks?: Task[];
                  inReviewLateTasks?: Task[];
                };
                const notStartedLate = data.notStartedLate ?? 0;
                const onTimeHeight = calculateBarHeight(selectedData.notStarted);
                const lateHeight = calculateBarHeight(notStartedLate);

                return (
                  <>
                    {selectedData.notStarted > 0 && (
                      <div
                        className="bar bar-ontime"
                        style={{
                          height: `${onTimeHeight}%`,
                          cursor: "pointer",
                        }}
                        onClick={() => handleNotStartedClick(selectedTab)}
                        onMouseEnter={(e) =>
                          handleShowPopover(
                            e as any,
                            renderPopoverContent(
                              selectedData.notStartedTasks,
                              "In Progress - On Time",
                              selectedData.notStarted
                            )
                          )
                        }
                        onMouseLeave={handleHidePopover}
                        onMouseMove={handleMouseMove}
                      />
                    )}
                    {notStartedLate > 0 && (
                      <div
                        className="bar bar-late"
                        style={{
                          height: `${lateHeight}%`,
                          bottom: `${onTimeHeight}%`,
                          cursor: "pointer",
                        }}
                        // UPDATED: Pass "IN PROGRESS,PENDING EXTENSION"
                        onClick={() => handleLateClick(selectedTab, "IN PROGRESS,PENDING EXTENSION")}
                        onMouseEnter={(e) =>
                          handleShowPopover(
                            e as any,
                            renderPopoverContent(
                              (data.notStartedLateTasks || []),
                              "In Progress - Late",
                              notStartedLate
                            )
                          )
                        }
                        onMouseLeave={handleHidePopover}
                        onMouseMove={handleMouseMove}
                      />
                    )}
                  </>
                );
              })()}
            </div>
            <span className="x-axis-tick"></span>
            <span className="bar-label">In Progress </span>
          </div>

          {/* Issues Found Bar */}
          <div className="bar-group">
            <div className="bar-wrapper">
              {selectedData && (() => {
                const data = selectedData as TaskData & {
                  notStartedLate?: number;
                  issuesFoundLate?: number;
                  inReviewLate?: number;
                  notStartedLateTasks?: Task[];
                  issuesFoundLateTasks?: Task[];
                  inReviewLateTasks?: Task[];
                };
                const issuesFoundLate = data.issuesFoundLate ?? 0;
                const onTimeHeight = calculateBarHeight(selectedData.issuesFound);
                const lateHeight = calculateBarHeight(issuesFoundLate);

                return (
                  <>
                    {selectedData.issuesFound > 0 && (
                      <div
                        className="bar bar-ontime"
                        style={{
                          height: `${onTimeHeight}%`,
                          cursor: "pointer",
                        }}
                        onClick={() => handleIssuesFoundClick(selectedTab)}
                        onMouseEnter={(e) =>
                          handleShowPopover(
                            e as any,
                            renderPopoverContent(
                              selectedData.issuesFoundTasks,
                              "Issues Found - On Time",
                              selectedData.issuesFound
                            )
                          )
                        }
                        onMouseLeave={handleHidePopover}
                        onMouseMove={handleMouseMove}
                      />
                    )}
                    {issuesFoundLate > 0 && (
                      <div
                        className="bar bar-late"
                        style={{
                          height: `${lateHeight}%`,
                          bottom: `${onTimeHeight}%`,
                          cursor: "pointer",
                        }}
                        // PASS "ISSUES FOUND" Explicitly
                        onClick={() => handleLateClick(selectedTab, "ISSUES FOUND")}
                        onMouseEnter={(e) =>
                          handleShowPopover(
                            e as any,
                            renderPopoverContent(
                              (data.issuesFoundLateTasks || []),
                              "Issues Found - Late",
                              issuesFoundLate
                            )
                          )
                        }
                        onMouseLeave={handleHidePopover}
                        onMouseMove={handleMouseMove}
                      />
                    )}
                  </>
                );
              })()}
            </div>
            <span className="x-axis-tick"></span>
            <span className="bar-label">Issues Found</span>
          </div>

          {/* In Review Bar */}
          <div className="bar-group">
            <div className="bar-wrapper">
              {selectedData && (() => {
                const data = selectedData as TaskData & {
                  notStartedLate?: number;
                  issuesFoundLate?: number;
                  inReviewLate?: number;
                  notStartedLateTasks?: Task[];
                  issuesFoundLateTasks?: Task[];
                  inReviewLateTasks?: Task[];
                };
                const inReviewLate = data.inReviewLate ?? 0;
                const onTimeHeight = calculateBarHeight(selectedData.inReview);
                const lateHeight = calculateBarHeight(inReviewLate);

                return (
                  <>
                    {selectedData.inReview > 0 && (
                      <div
                        className="bar bar-ontime"
                        style={{
                          height: `${onTimeHeight}%`,
                          cursor: "pointer",
                        }}
                        onClick={() => handleInReviewClick(selectedTab)}
                        onMouseEnter={(e) =>
                          handleShowPopover(
                            e as any,
                            renderPopoverContent(
                              selectedData.inReviewTasks,
                              "In Review - On Time",
                              selectedData.inReview
                            )
                          )
                        }
                        onMouseLeave={handleHidePopover}
                        onMouseMove={handleMouseMove}
                      />
                    )}
                    {inReviewLate > 0 && (
                      <div
                        className="bar bar-late"
                        style={{
                          height: `${lateHeight}%`,
                          bottom: `${onTimeHeight}%`,
                          cursor: "pointer",
                        }}
                        // UPDATED: Pass "IN REVIEW (PM),IN REVIEW (SUP),PENDING DELETION"
                        onClick={() => handleLateClick(selectedTab, "IN REVIEW (PM),IN REVIEW (SUP),PENDING DELETION")}
                        onMouseEnter={(e) =>
                          handleShowPopover(
                            e as any,
                            renderPopoverContent(
                              (data.inReviewLateTasks || []),
                              "In Review - Late",
                              inReviewLate
                            )
                          )
                        }
                        onMouseLeave={handleHidePopover}
                        onMouseMove={handleMouseMove}
                      />
                    )}
                  </>
                );
              })()}
            </div>
            <span className="x-axis-tick"></span>
            <span className="bar-label">In Review</span>
          </div>
        </div>
      </div>

      <div className="legend-container">
        <div className="legend-left">
          <div className="legend-item">
            <span className="legend-color legend-color-late"></span>
            <span className="legend-text">Late</span>
          </div>
          <div className="legend-item">
            <span className="legend-color legend-color-ontime"></span>
            <span className="legend-text">On time</span>
          </div>
        </div>
        <div className="legend-right">
          <span
            className="report-text cursor-pointer"
            onClick={() => navigate("/tasks?tags=90+L+Report")}
          >
            90 L Report
          </span>
        </div>
      </div>

      {/* Mouse-positioned popover */}
      {popoverState.show && (
        <div
          ref={popoverRef}
          className="popup-tooltip-progress"
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handleHidePopover}
          style={{
            left: `${popoverState.position.x}px`,
            top: `${popoverState.position.y}px`,
            transform: popoverState.position.y < 200 ? "translate(-50%, 0)" : "translate(-50%, -100%)",
            marginTop: popoverState.position.y < 200 ? "8px" : "-8px",
          }}
        >
          {popoverState.content}
        </div>
      )}
    </div>
  );
};

export default TaskProgress;